import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  linkedInApi,
  StatisticsOverview,
  ScheduledPostsOverview,
  PostStatsSummary,
  LinkedInStatus,
} from '../services/api';
import { FlowStepBar, LinkedInConnectionCard, useLinkedInFlow } from '../linkedin';
import { PageHeader, RelatedLinks, RelatedLinkItem } from '../components/ui';
import { SocialKonten } from '../social/SocialKonten';
import { ideaBacklinks } from '../linkedin/ideaLinks';
import { routeById } from '../navigation/routes';

/** Sprungziele eines veroeffentlichten Beitrags: Freigabe-Karte, Idee & Co., LinkedIn. */
function beitragsLinks(postId: string, postIdea: PostStatsSummary['postIdea'], url?: string | null): RelatedLinkItem[] {
  const links: RelatedLinkItem[] = [
    { key: 'queue', label: 'Freigabe', icon: 'send', to: `/scheduled-posts?id=${encodeURIComponent(postId)}`, title: 'In „Freigabe & Versand“ öffnen' },
    ...(postIdea ? ideaBacklinks(postIdea.id) : []),
  ];
  if (url) links.push({ key: 'live', label: 'Auf LinkedIn', icon: 'external-link', href: url, title: 'Beitrag auf LinkedIn öffnen' });
  return links;
}

const IdeeTitel: React.FC<{ postIdea: PostStatsSummary['postIdea'] }> = ({ postIdea }) => (postIdea ? (
  <div style={{ marginBottom: '8px' }}>
    <Link to={`/post-ideas?id=${encodeURIComponent(postIdea.id)}`}><strong>{postIdea.title}</strong></Link>
    {postIdea.topic && <span className="text-sm text-muted"> · {postIdea.topic}</span>}
  </div>
) : null);

const LinkedInStatisticsPage: React.FC = () => {
  const { refresh } = useLinkedInFlow();
  const [statistics, setStatistics] = useState<StatisticsOverview | null>(null);
  const [scheduledOverview, setScheduledOverview] = useState<ScheduledPostsOverview | null>(null);
  const [posts, setPosts] = useState<PostStatsSummary[]>([]);
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [months, setMonths] = useState(3);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'posts' | 'scheduled'>('overview');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, scheduledData, postsData, statusData] = await Promise.all([
        linkedInApi.getStatistics(months),
        linkedInApi.getScheduledOverview(),
        linkedInApi.getPostsWithMetrics({ months }),
        linkedInApi.getStatus(),
      ]);

      setStatistics(statsData);
      setScheduledOverview(scheduledData);
      setPosts(postsData.posts);
      setLinkedInStatus(statusData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSyncMetrics = async () => {
    try {
      setSyncing(true);
      await linkedInApi.syncMetrics();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Synchronisieren der Metriken');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Statistiken werden geladen...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="measure" />
      <PageHeader
        title="LinkedIn Statistiken"
        subtitle={routeById('li-stats')?.description}
        actions={(
          <>
            <select
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              className="form-select"
              style={{ width: 'auto' }}
            >
              <option value={1}>1 Monat</option>
              <option value={3}>3 Monate</option>
              <option value={6}>6 Monate</option>
              <option value={12}>12 Monate</option>
            </select>
            <button onClick={loadData} className="btn btn-info" disabled={loading}>
              Aktualisieren
            </button>
          </>
        )}
      >
        <p className="page-subtitle">
          Performance-Analyse der letzten {months} Monate
        </p>
      </PageHeader>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
          <button onClick={() => setError(null)} className="btn btn-sm btn-danger" style={{ marginLeft: '8px' }}>
            &times;
          </button>
        </div>
      )}

      {/* LinkedIn-Konto: eine Karte fuer alle Seiten */}
      <div style={{ marginBottom: '24px' }}>
        <LinkedInConnectionCard
          status={linkedInStatus}
          onChanged={() => { loadData(); refresh(); }}
          extraActions={(conn) => conn.isActive ? (
            <button onClick={handleSyncMetrics} className="btn btn-info btn-sm" disabled={syncing}>
              {syncing ? 'Synchronisiere…' : 'Metriken synchronisieren'}
            </button>
          ) : null}
        />
      </div>

      {/* Instagram, X und Facebook: Firmen- und persoenliche Konten */}
      <SocialKonten />

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid var(--color-border)' }}>
        {(['overview', 'posts', 'scheduled'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === tab ? 'var(--color-primary)' : 'transparent',
              color: activeTab === tab ? 'white' : 'var(--color-text-secondary)',
              fontWeight: activeTab === tab ? 600 : 400,
              cursor: 'pointer',
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s',
            }}
          >
            {tab === 'overview' && 'Übersicht'}
            {tab === 'posts' && 'Post-Performance'}
            {tab === 'scheduled' && 'Geplante Posts'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && statistics && (
        <div>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <KpiCard label="Veröffentlicht" value={statistics.totalPosts} color="var(--color-primary)" />
            <KpiCard label="Likes gesamt" value={statistics.totalLikes} color="var(--color-success)" />
            <KpiCard label="Kommentare gesamt" value={statistics.totalComments} color="var(--color-info)" />
            <KpiCard label="Shares gesamt" value={statistics.totalShares} color="var(--color-accent)" />
            <KpiCard label="Impressionen" value={statistics.totalImpressions} color="var(--color-warning)" />
            <KpiCard label="Avg. Engagement" value={statistics.averageEngagement.toFixed(1)} color="var(--color-primary-dark)" />
          </div>

          {/* Posts per Week Chart */}
          {statistics.postsPerWeek.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title" style={{ margin: 0 }}>Posts pro Woche</h3>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '200px' }}>
                  {statistics.postsPerWeek.map((week) => {
                    const maxCount = Math.max(...statistics.postsPerWeek.map((w) => w.count));
                    const height = maxCount > 0 ? (week.count / maxCount) * 160 : 0;
                    return (
                      <div key={week.week} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div className="text-xs" style={{ marginBottom: '4px' }}>{week.count}</div>
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '40px',
                            height: `${height}px`,
                            background: 'var(--color-primary)',
                            borderRadius: '4px 4px 0 0',
                            minHeight: '4px',
                          }}
                        />
                        <div className="text-xs text-muted" style={{ marginTop: '4px', transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap' }}>
                          {formatDate(week.week)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Engagement Trend */}
          {statistics.engagementTrend.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title" style={{ margin: 0 }}>Engagement-Verlauf</h3>
              </div>
              <div style={{ padding: '16px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Datum</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Likes</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Kommentare</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Shares</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.engagementTrend.map((day) => (
                      <tr key={day.date}>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>{formatDate(day.date)}</td>
                        <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>{day.likes}</td>
                        <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>{day.comments}</td>
                        <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>{day.shares}</td>
                        <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid var(--color-border-light)', fontWeight: 600 }}>
                          {day.likes + day.comments + day.shares}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Best Performing Post */}
          {statistics.bestPerformingPost && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h3 className="card-title" style={{ margin: 0 }}>Bester Post</h3>
              </div>
              <div style={{ padding: '16px' }}>
                <IdeeTitel postIdea={statistics.bestPerformingPost.postIdea} />
                <div className="code-block" style={{ marginBottom: '12px' }}>
                  {statistics.bestPerformingPost.content.substring(0, 300)}
                  {statistics.bestPerformingPost.content.length > 300 ? '...' : ''}
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {statistics.bestPerformingPost.latestMetrics && (
                    <>
                      <MetricBadge label="Likes" value={statistics.bestPerformingPost.latestMetrics.likes} />
                      <MetricBadge label="Kommentare" value={statistics.bestPerformingPost.latestMetrics.comments} />
                      <MetricBadge label="Shares" value={statistics.bestPerformingPost.latestMetrics.shares} />
                    </>
                  )}
                  <span className="text-sm text-muted">
                    Veröffentlicht: {statistics.bestPerformingPost.postedAt ? formatDate(statistics.bestPerformingPost.postedAt) : '-'}
                  </span>
                </div>
                <RelatedLinks
                  className="mt-3"
                  items={beitragsLinks(statistics.bestPerformingPost.postId, statistics.bestPerformingPost.postIdea, statistics.bestPerformingPost.linkedInPostUrl)}
                  label="Verknüpft mit dem besten Post"
                />
              </div>
            </div>
          )}

          {statistics.totalPosts === 0 && (
            <div className="empty-state">
              <div className="empty-state-title">Keine veröffentlichten Posts im ausgewählten Zeitraum.</div>
              <p className="text-muted">Veröffentliche Beiträge über <Link to="/scheduled-posts">Freigabe &amp; Versand</Link>, um Statistiken zu sehen.</p>
            </div>
          )}
        </div>
      )}

      {/* Posts Performance Tab */}
      {activeTab === 'posts' && (
        <div>
          {posts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-title">Keine Posts mit Metriken gefunden.</div>
            </div>
          ) : (
            <div>
              {posts.map((post) => (
                <div key={post.postId} className="card" style={{ marginBottom: '16px' }}>
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <IdeeTitel postIdea={post.postIdea} />
                        <div className="code-block" style={{ marginBottom: '8px', maxHeight: '100px', overflow: 'hidden' }}>
                          {post.content.substring(0, 200)}{post.content.length > 200 ? '...' : ''}
                        </div>
                        <div className="text-xs text-muted">
                          Geplant: {formatDate(post.scheduledDate)} |
                          Veröffentlicht: {post.postedAt ? formatDateTime(post.postedAt) : '-'}
                        </div>
                      </div>
                    </div>
                    {post.latestMetrics ? (
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <MetricBadge label="Impressionen" value={post.latestMetrics.impressions} />
                        <MetricBadge label="Likes" value={post.latestMetrics.likes} />
                        <MetricBadge label="Kommentare" value={post.latestMetrics.comments} />
                        <MetricBadge label="Shares" value={post.latestMetrics.shares} />
                        <MetricBadge label="Klicks" value={post.latestMetrics.clicks} />
                        <span className="text-xs text-muted">
                          Zuletzt aktualisiert: {formatDateTime(post.latestMetrics.fetchedAt)}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-muted">
                        Noch keine Metriken vorhanden. Metriken synchronisieren, um Daten abzurufen.
                      </div>
                    )}
                    <RelatedLinks
                      className="mt-3"
                      items={beitragsLinks(post.postId, post.postIdea, post.linkedInPostUrl)}
                      label="Verknüpft mit diesem Post"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scheduled Posts Tab */}
      {activeTab === 'scheduled' && scheduledOverview && (
        <div>
          {/* Status Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <KpiCard label="Ausstehend" value={scheduledOverview.pending} color="var(--color-warning)" />
            <KpiCard label="Genehmigt" value={scheduledOverview.approved} color="var(--color-success)" />
            <KpiCard label="Abgelehnt" value={scheduledOverview.rejected} color="var(--color-error)" />
            <KpiCard label="Veröffentlicht" value={scheduledOverview.posted} color="var(--color-info)" />
          </div>

          {/* Upcoming Posts */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ margin: 0 }}>Nächste geplante Posts</h3>
            </div>
            <div style={{ padding: '16px' }}>
              {scheduledOverview.upcoming.length === 0 ? (
                <div className="text-muted">Keine anstehenden Posts.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Datum</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid var(--color-border)' }}>Vorschau</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledOverview.upcoming.map((post) => (
                      <tr key={post.id}>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border-light)', whiteSpace: 'nowrap' }}>
                          {formatDateTime(post.scheduledDate)}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>
                          <span className={`badge ${post.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>
                            {post.status === 'approved' ? 'Genehmigt' : 'Ausstehend'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--color-border-light)' }}>
                          <IdeeTitel postIdea={post.postIdea} />
                          <Link to={`/scheduled-posts?id=${encodeURIComponent(post.id)}`} title="In „Freigabe & Versand“ öffnen">
                            {post.contentPreview}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components

interface KpiCardProps {
  label: string;
  value: number | string;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, color }) => (
  <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
    <div style={{ fontSize: '32px', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
    <div className="text-sm text-muted" style={{ marginTop: '4px' }}>{label}</div>
  </div>
);

interface MetricBadgeProps {
  label: string;
  value: number;
}

const MetricBadge: React.FC<MetricBadgeProps> = ({ label, value }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '12px',
    background: 'var(--color-bg-secondary)',
    fontSize: '13px',
  }}>
    <span style={{ fontWeight: 600 }}>{value}</span>
    <span className="text-muted">{label}</span>
  </div>
);

export default LinkedInStatisticsPage;
